// Research desk API (TASK-610/612) — dossiers + citation drill-down.
// All calls ride httpClient (Bearer + X-Account-ID injected).
import { httpClient, httpClientLongRunning } from '../../lib/httpClient';

export interface DossierSource {
  n: number;
  title: string;
  issuer: string | null;
  origin: string;
  published_at: string | null;
  page: number | null;
  doc_id: string;
}

export interface BookImpactClient {
  client_id: string;
  name: string;
  exposure_chf: number | null;
  position_count: number;
  kg_conflicts: string[];
  kg_matches: string[];
}

export interface BookImpact {
  clients: BookImpactClient[];
  total_exposure_chf: number | null;
  has_unknown_exposure: boolean;
  position_count: number;
}

export interface ResearchDossier {
  dossier_id: number;
  subject: { type: 'company' | 'industry'; name: string; isins: string[] };
  thesis: string;
  sections: { title: string; md: string }[];
  model_view: string;
  book_impact: BookImpact;
  sources: DossierSource[];
  caveat: string;
  confidence: number;
  created_at: string | null;
  fresh?: boolean;
}

export interface SourceChunk {
  chunk_id: string;
  page: number | null;
  text: string;
}

class ResearchApiService {
  /** Latest dossier for a subject; null when none exists yet (widget then generates). */
  async getLatestDossier(
    subjectType: 'company' | 'industry',
    subject: string,
  ): Promise<ResearchDossier | null> {
    try {
      return await httpClient.get<ResearchDossier>('/api/research-dossier/latest', {
        params: { subject_type: subjectType, subject },
      });
    } catch (err: unknown) {
      if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
      throw err;
    }
  }

  /** Generate a fresh dossier (one LLM call server-side — long-running client). */
  async generateDossier(
    subjectType: 'company' | 'industry',
    subject: string,
  ): Promise<ResearchDossier | { success: false; error: string }> {
    return await httpClientLongRunning.post('/api/research-dossier/generate', {
      subject_type: subjectType,
      subject,
    });
  }

  /** TASK-613: assemble the FinSA rationale-note draft as a versioned workbench doc. */
  async draftRationaleNote(
    dossierId: number,
    clientId: string,
  ): Promise<{ document_id: string; version: number; url: string }> {
    return await httpClientLongRunning.post('/api/workbench/documents/rationale-note', {
      dossier_id: dossierId,
      client_id: clientId,
    });
  }

  /** Chunk excerpts of one research source, optionally a single page (citation panel). */
  async getSourceChunks(docId: string, page?: number | null): Promise<SourceChunk[]> {
    const res = await httpClient.get<{ chunks: SourceChunk[] }>(
      `/api/research/sources/${docId}/chunks`,
      { params: page != null ? { page } : {} },
    );
    return res.chunks;
  }
}

export const researchApiService = new ResearchApiService();
